/**
 * Deterministic per-agent visual identity.
 *
 * An agent's colour is derived from its channel hash rather than assigned from a
 * table, so a newly-registered agent gets a stable colour with no coordination and
 * the same agent is the same colour in the sidebar, the roster, its message avatars
 * and the busy strip. See docs/UI-COMPONENTS.md and the "Agent identity" card in the
 * Avengents UI design project.
 */

/** Golden angle, in degrees — spreads adjacent hashes far apart on the hue wheel. */
const GOLDEN_ANGLE = 137.508

/** Fixed lightness/chroma so every hue reads equally bright and the set looks like
 *  one system. OKLCH is perceptually uniform, which is the whole reason for it. */
export const AGENT_L = 0.72
export const AGENT_C = 0.16

/**
 * Hue in [0, 360) derived from a channel hash (hex, e.g. `d2e5664201eb`).
 * Falls back to hashing the raw string when the value isn't hex, so display names
 * still get a stable colour.
 */
export function agentHue(channelHash: string): number {
  const head = channelHash.slice(0, 8)
  let n = Number.parseInt(head, 16)
  if (!Number.isFinite(n)) {
    n = 0
    for (const ch of channelHash) n = (n * 31 + ch.codePointAt(0)!) >>> 0
  }
  return (n * GOLDEN_ANGLE) % 360
}

/** `oklch(...)` accent for an agent. `alpha` < 1 yields the tint used for fills. */
export function agentColor(channelHash: string, alpha = 1): string {
  const h = agentHue(channelHash).toFixed(1)
  return alpha >= 1
    ? `oklch(${AGENT_L} ${AGENT_C} ${h})`
    : `oklch(${AGENT_L} ${AGENT_C} ${h} / ${alpha})`
}

/**
 * Two-letter monogram from the identifying segment of an agent name.
 *
 * Deliberately not initials: every agent here is prefixed `avengents-` and suffixed
 * with its kind (`-claude`), so initials would collide on every one of them.
 * `avengents-app-claude` → `AP`.
 */
const KIND_SUFFIXES = new Set(['claude', 'cursor', 'codex', 'grok', 'hermes', 'muse', 'agent', 'test'])

export function agentMonogram(name: string): string {
  const parts = name.split(/[-_.\s]+/).filter(Boolean)
  const meaningful = parts.filter(
    (p) => p.toLowerCase() !== 'avengents' && !KIND_SUFFIXES.has(p.toLowerCase()),
  )
  const source = (meaningful[0] ?? parts[0] ?? '?').toUpperCase()
  return source.slice(0, 2)
}

/** `claude` | `cursor` | `codex` | null — the agent kind, read off the name. */
export function agentKind(name: string): string | null {
  const lower = name.toLowerCase()
  for (const kind of ['claude', 'cursor', 'codex', 'grok', 'hermes', 'muse']) {
    if (lower.includes(kind)) return kind
  }
  return null
}
